import React, { useMemo } from 'react';

import { cn } from '@epoch-ui/lib/utils';
import dayjs from 'utils/extendedDayJS';
import { EVENT_VIEW_DATE_FORMAT } from 'constants/dates';

const formatEventDateTime = (
  datetimeScheduled: string,
  datetimeEnd: string,
  allDay: boolean,
  eventTimezone = 'UTC',
  displayedTimezone = 'UTC',
  showTime = true
): string => {
  const startDatetime = dayjs.utc(datetimeScheduled).tz(allDay ? eventTimezone : displayedTimezone);
  const endDatetime = dayjs.utc(datetimeEnd).tz(allDay ? eventTimezone : displayedTimezone);

  let formattedDate = startDatetime.format(EVENT_VIEW_DATE_FORMAT);
  if (!startDatetime.isSame(endDatetime, 'day')) {
    formattedDate += ` – ${endDatetime.format(EVENT_VIEW_DATE_FORMAT)}`;
  }
  const formattedTime = dayjs.formatEventTime({
    startDatetime,
    endDatetime,
    allDay,
    displayedTimezone
  });

  let result = formattedDate;

  if (showTime) {
    result += ` • ${formattedTime}`;
  }

  return result;
};

type SessionForCard = {
  title: string;
  datetimeScheduled: string;
  datetimeEnd: string;
  allDay: boolean;
};

type SessionCardProviderValue = {
  session: SessionForCard;
  eventTimezone: string;
  displayedTimezone?: string;
};

const SessionCardContext = React.createContext<SessionCardProviderValue | undefined>(undefined);

export const useSessionCardInfo = (): SessionCardProviderValue => {
  const context = React.useContext(SessionCardContext);
  if (!context) throw new Error('useSessionCardInfo must be used within a SessionCardProvider');
  return context;
};

export interface SessionCardProps extends React.HTMLAttributes<HTMLDivElement> {
  session: SessionForCard;
  eventTimezone: string;
  displayedTimezone?: string;
}

/**
 * Render a Session Card.
 * @returns
 */
export const SessionCard = React.forwardRef<HTMLDivElement, SessionCardProps>(
  ({ session, eventTimezone, displayedTimezone, className, ...rest }, ref) => {
    const contextValue = useMemo(
      () => ({ session, eventTimezone, displayedTimezone }),
      [session, eventTimezone, displayedTimezone]
    );

    return (
      <SessionCardContext.Provider value={contextValue}>
        <div
          ref={ref}
          className={cn(
            'tw-flex tw-size-full tw-flex-row tw-items-center tw-justify-between tw-rounded-xl tw-border-[1.5px] tw-border-solid tw-border-border-grey tw-px-4 tw-py-4',
            (rest.onClick || rest.role === 'button') && 'tw-cursor-pointer',
            className
          )}
          {...rest}
        />
      </SessionCardContext.Provider>
    );
  }
);

export const SessionCardTitleAndDate = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...rest }, ref) => {
  const {
    session: { title, datetimeScheduled, datetimeEnd, allDay },
    eventTimezone,
    displayedTimezone
  } = useSessionCardInfo();

  const timeString = formatEventDateTime(
    datetimeScheduled,
    datetimeEnd,
    allDay,
    eventTimezone,
    displayedTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone
  );

  return (
    <div ref={ref} className={cn('tw-flex tw-flex-row tw-items-center', className)} {...rest}>
      <div className="tw-text-left tw-text-sm">
        <div className="tw-font-medium">{title}</div>
        <div className="tw-mt-1 tw-font-normal tw-leading-5 tw-text-gray-600">{timeString}</div>
      </div>
    </div>
  );
});
